import throttle from 'lodash.throttle';
import { useTranslation } from 'react-i18next';

import { FirebaseService, CloudFunctionsService } from '@services';
import { useStore } from '@stores';
import {
  OfflineDefaultSessionTimeoutInMinutes,
  STATUS_OPTIONS,
  sendEventToHost,
} from '@utils';

import { useFirebaseRemoteConfig } from '.';

/**
 * A custom hook to handle inactivity timers for user sessions.
 * This hook sets up an inactivity timer that will trigger a session timeout if no user activity is detected.
 * It uses Firebase Remote Config to fetch the session timeout duration.
 *
 * @returns {Object} An object containing the following properties:
 *   - inactivityTimer: A function that sets up the inactivity timer for the user session.
 *   - firebaseLoading: A boolean representing the loading state of Firebase Remote Config data.
 */
export const useActivityTimer = () => {
  /**
   * The session timeout duration fetched from Firebase Remote Config.
   * @type {number}
   */
  const { firebaseValue: timeToWait, firebaseLoading } =
    useFirebaseRemoteConfig(
      'session_timeout',
      OfflineDefaultSessionTimeoutInMinutes
    );

  const { userStore, formStore } = useStore();
  const timeToWaitInMinutes = Number(timeToWait) || 0;

  /**
   * The translation function from the 'common' translation namespace.
   * @type {Function}
   */
  const { t } = useTranslation('common');

  /**
   * An instance of the FirebaseService used to handle Firebase-related operations.
   * @type {FirebaseService}
   */
  const cloudFunctionService = new CloudFunctionsService();
  const firebaseService = new FirebaseService(cloudFunctionService);

  /**
   * Sets up the inactivity timer and handles user session timeout.
   */
  const inactivityTimer = () => {
    let time: NodeJS.Timeout;
    const events = [
      'mousedown',
      'mousemove',
      'mouseup',
      'keypress',
      'keyup',
      'keydown',
      'scroll',
      'touchstart',
      'touchmove',
      'touchend',
      'click',
    ];

    /**
     * Ends the user session and performs necessary actions.
     */
    const endSession = async () => {
      const exists = await firebaseService.doesEngagementExist(userStore.user!);
      if (exists) {
        await firebaseService.postUserTimeout(userStore.user!);
        if (formStore.currentModuleName === 'MODULE_RESULT_OPTIONS') {
          await firebaseService.updateEngagementStatus(
            userStore.user!,
            STATUS_OPTIONS.READY_FOR_SUBMISSION
          );
        }
        alert(t('sessionExpired'));
        sendEventToHost('close');
      } else {
        alert(t('sessionExpired'));
        sendEventToHost('close');
      }
    };

    /**
     * Throttles the resetTimer function to avoid unnecessary calls within a specified interval.
     */
    const resetTimer = throttle(() => {
      clearTimeout(time);
      time = setTimeout(endSession, timeToWaitInMinutes * 60 * 1000);
      // Calls function a maximum of one time every minute
    }, 60000);

    // Attach event listeners to monitor user activity
    events.forEach((name) => {
      window.addEventListener(name, resetTimer, true);
    });
  };

  return { inactivityTimer, firebaseLoading };
};
